import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Clock, X } from 'lucide-react';

export type PublicService = {
    id: string;
    name: string;
    description?: string;
    duration?: number;
    price?: number;
    requiresSubscription?: boolean;
    images: string[];
    attendants: { id: string; name: string; photoUrl?: string }[];
};

type ServiceModalProps = {
    loginUrl: string;
    service: PublicService;
    money: (value?: number) => string;
    duration: (minutes?: number) => string;
    initials: (name: string) => string;
    onClose: () => void;
};

// Bottom sheet no mobile e dialogo centralizado no desktop.
export function ServiceModal({ loginUrl, service, money, duration, initials, onClose }: ServiceModalProps) {
    const [active, setActive] = useState(0);
    const [visible, setVisible] = useState(false);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useEffect(() => {
        const frame = requestAnimationFrame(() => setVisible(true));
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeRef.current();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);
        return () => {
            cancelAnimationFrame(frame);
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    return createPortal(
        <div
            className={`fixed inset-0 z-[900] flex items-end justify-center bg-black/45 backdrop-blur-[2px] transition-opacity duration-200 sm:items-center sm:p-6 ${
                visible ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="service-modal-title"
                className={`relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[1.75rem] bg-base-100 shadow-2xl transition-transform duration-300 ease-out sm:max-w-lg sm:rounded-[1.75rem] ${
                    visible ? 'translate-y-0 sm:scale-100' : 'translate-y-8 sm:translate-y-0 sm:scale-95'
                }`}
                onClick={(event) => event.stopPropagation()}
            >
                <span className="absolute left-1/2 top-2 z-10 h-1 w-9 -translate-x-1/2 rounded-full bg-white/70 sm:hidden" aria-hidden></span>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fechar"
                    className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-sm text-white backdrop-blur-md hover:bg-black/55"
                >
                    <X aria-hidden="true" />
                </button>

                <div className="overflow-y-auto overscroll-contain">
                    {service.images.length ? (
                        <div className="relative">
                            <div
                                className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                                onScroll={(event) => {
                                    const el = event.currentTarget;
                                    setActive(Math.round(el.scrollLeft / el.clientWidth));
                                }}
                            >
                                {service.images.map((src, index) => (
                                    <img
                                        key={`${src}-${index}`}
                                        src={src}
                                        alt={`${service.name} — foto ${index + 1}`}
                                        className="aspect-[4/3] w-full shrink-0 snap-center bg-base-200 object-cover"
                                    />
                                ))}
                            </div>
                            {service.images.length > 1 && (
                                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                                    {service.images.map((_, index) => (
                                        <span
                                            key={index}
                                            className={`h-1.5 rounded-full bg-white transition-all ${index === active ? 'w-4 opacity-100' : 'w-1.5 opacity-60'}`}
                                        ></span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="h-14" aria-hidden></div>
                    )}

                    <div className="space-y-6 px-6 pb-6 pt-5">
                        <div>
                            <h2 id="service-modal-title" className="text-2xl font-semibold tracking-tight">{service.name}</h2>
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-xl font-semibold tabular-nums">{money(service.price)}</span>
                                {!!service.duration && (
                                    <span className="rounded-full bg-base-200 px-2.5 py-1 text-xs font-medium text-base-content/70">
                                        <Clock className="mr-1" aria-hidden="true" />{duration(service.duration)}
                                    </span>
                                )}
                                {service.requiresSubscription && (
                                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">Exclusivo para assinantes</span>
                                )}
                            </div>
                        </div>

                        {service.description && (
                            <p className="whitespace-pre-line text-[0.9375rem] leading-relaxed text-base-content/70">{service.description}</p>
                        )}

                        {!!service.attendants.length && (
                            <div>
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-base-content/45">Quem realiza</h3>
                                <ul className="mt-3 flex flex-wrap gap-2">
                                    {service.attendants.map((attendant) => (
                                        <li key={attendant.id} className="flex items-center gap-2 rounded-full bg-base-200 py-1 pl-1 pr-3 text-sm">
                                            <span className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-base-300 text-[0.625rem] font-semibold text-base-content/55">
                                                {initials(attendant.name)}
                                                {attendant.photoUrl && (
                                                    <img src={attendant.photoUrl} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(event) => { event.currentTarget.hidden = true; }} />
                                                )}
                                            </span>
                                            {attendant.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <p className="text-xs text-base-content/45">Valor de referência. Confirme condições e disponibilidade ao agendar.</p>
                    </div>
                </div>

                <div className="border-t border-base-content/[0.06] px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4">
                    <Link to={loginUrl} className="btn btn-primary h-12 w-full rounded-full text-[0.9375rem]">Agendar este serviço</Link>
                </div>
            </div>
        </div>,
        document.body,
    );
}
